import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { Loader } from 'features/command-center/components/shared/Widget/Loader';
import { CALL_TYPES, ENDPOINTS, TASK_TYPES } from 'features/daily-plan/constants';
import { useDailyPlanRoles } from 'features/daily-plan/hooks';
import { useShowAlert } from 'hooks';
import isEmpty from 'lodash/isEmpty';
import API from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import SendoutManager from 'UI/components/organisms/sendouts';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { MAX_TASKS_LIMIT } from 'UI/pages/DailyPlan/DailyPlan.constants';
import { persistTeamSelectors } from 'UI/pages/DailyPlan/DailyPlan.utils';
import { getErrorMessage } from 'UI/utils';

import { CallTask, MarketCandidate, OtherTask, RecruitJobOrder } from '../../forms';
import { TeamSelectors } from '../../forms/shared/TeamSelectors';

import { FORM_FIELDS_MAP } from './CreationTaskDrawer.constants';
import { useStyles } from './CreationTaskDrawer.styles';
import {
  formatCallingTaskToPreload,
  getPrefillForm,
  isCallingTask,
  TASK_CONFIG
} from './CreationTaskDrawer.utils';

const { form: COPIES } = strings.dailyPlan.drawer;

const getFormToRender = ({
  entityData,
  entityType,
  handleSendoutDetailsClick,
  isFetchingInterview,
  isMultiple,
  taskTypeId,
  disableTime
}) => {
  const initialInterviewSearch =
    entityType === EntityType.HiringAuthority && isCallingTask(taskTypeId)
      ? entityData?.companyName
      : entityData?.name;

  const taskFormMap = {
    [TASK_TYPES.recruitJobOrder]: (
      <RecruitJobOrder entityType={entityType} isMultiple={isMultiple} disableTime={disableTime} />
    ),
    [TASK_TYPES.marketCandidate]: (
      <MarketCandidate entityType={entityType} isMultiple={isMultiple} disableTime={disableTime} />
    ),
    [TASK_TYPES.debriefCall]: (
      <CallTask
        callType={CALL_TYPES.debrief}
        initialInterviewSearch={initialInterviewSearch}
        isFetchingInterview={isFetchingInterview}
        onSendoutDetailsClick={handleSendoutDetailsClick}
      />
    ),
    [TASK_TYPES.preparationCall]: (
      <CallTask
        callType={CALL_TYPES.preparation}
        initialInterviewSearch={initialInterviewSearch}
        isFetchingInterview={isFetchingInterview}
        onSendoutDetailsClick={handleSendoutDetailsClick}
      />
    ),
    [TASK_TYPES.closingCall]: (
      <CallTask
        callType={CALL_TYPES.closing}
        initialInterviewSearch={initialInterviewSearch}
        isFetchingInterview={isFetchingInterview}
        onSendoutDetailsClick={handleSendoutDetailsClick}
      />
    ),
    [TASK_TYPES.other]: (
      <OtherTask entityType={entityType} isMultiple={isMultiple} disableTime={disableTime} />
    ),
    default: null
  };

  return taskFormMap[taskTypeId] ?? taskFormMap.default;
};

export const CreationTaskDrawer = ({
  allowedTaskTypes = [],
  defaultValues = {},
  entityData = null,
  entityType = null,
  listItems = [],
  onClose,
  onTeamFiltersChange = null,
  onTaskUpdated,
  onTaskCreated,
  tasksQuery = null,
  totalSelected = 0
}) => {
  const [selectedSendoutId, setSelectedSendoutId] = useState(null);
  const { showAlert } = useShowAlert();
  const classes = useStyles();

  const [uiState, setUIState] = useState({
    isLoading: false,
    isSaving: false,
    isFetchingInterview: false
  });

  const { shouldShowTeamSelector, shouldShowRecruiterSelector } = useDailyPlanRoles();

  const isLimitExceeded = totalSelected > MAX_TASKS_LIMIT;
  const isExcludeSelection = !!tasksQuery;

  const form = useForm({ defaultValues });
  const { handleSubmit, watch, setValue, register, unregister, reset } = form;

  const formValues = watch();

  const taskType = formValues[FORM_FIELDS_MAP.TaskType.key];
  const taskId = formValues[FORM_FIELDS_MAP.TaskId.key];

  const handleSelectChange = (name?, value) => {
    if (isCallingTask(value?.id)) {
      setValue(FORM_FIELDS_MAP.Interview.key, null);
    }
    setValue(FORM_FIELDS_MAP.TaskType.key, value);
  };

  const handleSendoutDetailsClick = sendoutId => setSelectedSendoutId(sendoutId);

  const syncFilters = () => {
    const selectedTeam = formValues[FORM_FIELDS_MAP.Team.key];
    const selectedRecruiter = formValues[FORM_FIELDS_MAP.Recruiter.key];
    const newTeamFilters = {
      ...(selectedTeam && { coach: { paramName: 'coachIds', value: selectedTeam } }),
      ...(selectedRecruiter && {
        recruiter: { paramName: 'assignedTo', value: selectedRecruiter }
      })
    };
    persistTeamSelectors(newTeamFilters);
    onTeamFiltersChange && onTeamFiltersChange(newTeamFilters);
  };

  const closeSendout = () => {
    setSelectedSendoutId(null);
  };

  const handleSendoutSubmit = async data => {
    if (!data) return;

    try {
      const interviewId = formValues?.[FORM_FIELDS_MAP.Interview.key]?.id;
      const endpoint = Endpoints.SendoutInterviewDetails.replace(':id', interviewId);
      setUIState(prev => ({ ...prev, isFetchingInterview: true }));
      const interviewResponse = await API.get(endpoint, { apiVersion: 2 });

      if (interviewResponse.status === HTTPStatusCodes.Ok) {
        const interviewDate = interviewResponse?.data?.interviewDate;
        const hiringAuthorityName = interviewResponse?.data?.hiringAuthorityName;
        const hiringAuthorityId = interviewResponse?.data?.hiringAuthorityId;
        setValue(FORM_FIELDS_MAP.Interview.key, {
          ...formValues[FORM_FIELDS_MAP.Interview.key],
          interviewDate,
          hiringAuthorityName,
          hiringAuthorityId
        });
      }
    } catch (error) {
      showAlert({
        title: COPIES.alert.error,
        autoHideDuration: 5000,
        body: COPIES.alert.error,
        error
      });
    }
    setUIState(prev => ({ ...prev, isFetchingInterview: false }));
  };

  const onSubmit = async formData => {
    try {
      setUIState(prev => ({
        ...prev,
        isSaving: true
      }));
      const taskTypeId = taskType?.id;
      const taskConfig = TASK_CONFIG[taskTypeId];

      const response = await taskConfig.handler(formData);

      if (response.status === HTTPStatusCodes.Created || response.status === HTTPStatusCodes.Ok) {
        const isUpdated = response.status === HTTPStatusCodes.Ok;
        const isCreated = response.status === HTTPStatusCodes.Created;
        const actionText = isCreated ? 'created' : 'updated';

        showAlert({
          title: strings.formatString(COPIES.alert.success, { action: actionText }),
          autoHideDuration: 5000
        });
        setUIState(prev => ({
          ...prev,
          isSaving: false
        }));

        if (isUpdated) {
          onTaskUpdated && onTaskUpdated();
        }

        if (isCreated) {
          shouldShowRecruiterSelector && onTaskCreated
            ? onTaskCreated(syncFilters)
            : onTaskCreated();
        }
      }
    } catch (error) {
      showAlert({
        severity: 'error',
        title: COPIES.alert.error,
        autoHideDuration: 5000,
        body: getErrorMessage(error, error?.message ?? COPIES.alert.error)
      });
      setUIState(prev => ({
        ...prev,
        isSaving: false
      }));
    }
  };

  useEffect(() => {
    register(FORM_FIELDS_MAP.TaskId.key);
    register(FORM_FIELDS_MAP.TaskType.key);
    register({ name: FORM_FIELDS_MAP.TasksQuery.key, value: tasksQuery });

    return () => {
      unregister(FORM_FIELDS_MAP.TaskId.key);
      unregister(FORM_FIELDS_MAP.TaskType.key);
      unregister(FORM_FIELDS_MAP.TasksQuery.key);
    };
  }, [tasksQuery, register, unregister]);

  useEffect(() => {
    if (totalSelected > 0) {
      register({ name: FORM_FIELDS_MAP.ListItems.key, value: listItems });
    }

    return () => {
      if (totalSelected > 0) {
        unregister(FORM_FIELDS_MAP.ListItems.key);
      }
    };
  }, [listItems, totalSelected, register, unregister]);

  useEffect(() => {
    if (isEmpty(defaultValues)) return;

    const taskTypeId = defaultValues?.taskType?.id;

    if (isCallingTask(taskTypeId)) {
      reset(formatCallingTaskToPreload(defaultValues));
      return;
    }

    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!entityData || !entityType || !taskType) return;

    const prefillForm = getPrefillForm({
      taskTypeId: taskType.id,
      entityData,
      entityType,
      setValue
    });
    if (prefillForm) prefillForm();
  }, [entityData, entityType, taskType, setValue]);

  const handleCloseDrawer = () => {
    onClose({ refreshData: false });
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: handleCloseDrawer
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: !taskType,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{
          title: onTaskUpdated ? COPIES.titles.editTask : COPIES.titles.createTask
        }}
        ContentWrapper="form"
        ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
        onClose={handleCloseDrawer}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <FormContext {...form}>
          <When condition={isLimitExceeded}>
            <Alert className={classes.alert} severity="warning">
              {strings.formatString(COPIES.multipleCreation.taskLimitExceeded, {
                totalSelected,
                maxItemsLimit: MAX_TASKS_LIMIT
              })}
            </Alert>
          </When>

          <When condition={totalSelected > 0}>
            <Typography variant="subtitle2" component="h6" gutterBottom>
              <Typography component="span" className={classes.countLabel}>
                {totalSelected}
              </Typography>
              {isExcludeSelection
                ? COPIES.multipleCreation.contactsTotal
                : COPIES.multipleCreation.total}
            </Typography>
            <Divider />
          </When>
          <When condition={(shouldShowRecruiterSelector || shouldShowTeamSelector) && !taskId}>
            <TeamSelectors />
          </When>
          <Typography variant="subtitle1">
            <b>{COPIES.labels.taskType}</b>
          </Typography>
          <AutocompleteSelect
            name={FORM_FIELDS_MAP.TaskType.key}
            selectedValue={taskType}
            placeholder={COPIES.placeholders.taskType}
            url={ENDPOINTS.taskTypes}
            onSelect={handleSelectChange}
            disabled={!!taskId}
            filterOptions={
              allowedTaskTypes.length > 0
                ? options => {
                    return options.filter(option => allowedTaskTypes.includes(option.id));
                  }
                : opts => opts
            }
            renderOption={option => (
              <div>
                <Typography variant="body1">{option?.title}</Typography>
                {option?.description && (
                  <Typography variant="body2" color="textSecondary">
                    {option.description}
                  </Typography>
                )}
              </div>
            )}
          />

          {uiState.isLoading ? (
            <Loader />
          ) : (
            getFormToRender({
              taskTypeId: taskType?.id,
              handleSendoutDetailsClick,
              isFetchingInterview: uiState.isFetchingInterview,
              entityType,
              entityData,
              isMultiple: totalSelected > 0 || !!tasksQuery,
              disableTime: totalSelected > 1 || !!tasksQuery
            })
          )}
        </FormContext>
      </FPDrawer>
      {!!selectedSendoutId && (
        <Drawer open={!!selectedSendoutId} onClose={closeSendout}>
          <div role="presentation">
            <SendoutManager
              onEdit={handleSendoutSubmit}
              onClose={closeSendout}
              id={selectedSendoutId}
            />
          </div>
        </Drawer>
      )}
    </>
  );
};
